import { apiCustomer } from "@/api"
import type { Address } from "@/types"

/** Enumerated verdict of merchant-service-core QuoteDelivery; the UI maps it to copy, never parses text. */
export type DeliveryQuoteReason =
  | "QUOTE_OK"
  | "OUTSIDE_AREA"
  | "ADDRESS_NOT_FOUND"
  | "STORE_CLOSED"
  | "STORE_PAUSED"
  | "DELIVERY_NOT_OFFERED"

/** Server-authoritative delivery quote for one address: zone, fee in cents, estimate and store status. */
export type DeliveryQuote = {
  deliverable: boolean
  reason: DeliveryQuoteReason
  reasonCode?: string
  area?: { id: string; name: string }
  feeCents: number
  etaMinutes: number
  store: {
    open: boolean
    paused: boolean
    nextOpeningAt?: string
    acceptsScheduled: boolean
    closedReason?: string
  }
  quotedAt: string
}

const hasPin = (address: Address) =>
  Number.isFinite(address.latitude) &&
  Number.isFinite(address.longitude) &&
  !(address.latitude === 0 && address.longitude === 0)

/** Quotes a saved address via the gateway; `scheduled` only lifts the open-now check; null means fall back to the local preview. */
export async function fetchDeliveryQuote(
  merchantId: string,
  address: Address,
  scheduled: boolean,
): Promise<DeliveryQuote | null> {
  try {
    const { data } = await apiCustomer.post<DeliveryQuote>(`/merchants/${merchantId}/delivery-quote`, {
      type: "DELIVERY",
      scheduledFor: scheduled ? new Date(Date.now() + 60 * 60 * 1000).toISOString() : undefined,
      address: {
        street: address.street,
        number: address.number,
        district: address.district,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        complement: address.complement,
        coordinates: hasPin(address)
          ? { latitude: address.latitude, longitude: address.longitude }
          : undefined,
      },
    })
    return data && typeof data.reason === "string" ? data : null
  } catch {
    return null
  }
}
